// DNAChat · Painel de Configuração WhatsApp Business (V4.54)
// - Config Cloud API (Business Account, Phone Number, Access Token, Verify Token, App Secret, API version)
// - Sincronização de templates
// - Métricas (enviadas, entregues, lidas, falhas, fila, inbound)
// - Segredos são exibidos redacted (***abcd); ao salvar, placeholder não sobrescreve valor original.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Save, RefreshCw, CheckCircle2, XCircle, Loader2,
  Send, Users, Bell, Database, Copy, Link2, Power, Zap, ListChecks, AlertTriangle, X, RotateCw, ChevronRight,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const emptyCfg = {
  business_account_id: '', phone_number_id: '', access_token: '',
  verify_token: '', app_secret: '', api_version: 'v20.0',
  webhook_url: '', enabled: false,
  // V4.58 · flags de configuração vindas do backend
  access_token_configured: false,
  webhook_verify_token_configured: false,
  app_secret_configured: false,
};

export default function WhatsAppConfigPanel() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(emptyCfg);
  const [loaded, setLoaded] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [syncing, setSyncing] = useState(false);
  // V4.56 · teste ping-pong
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // V4.68 · Validação de permissão de envio (131005 e afins)
  const [validating, setValidating] = useState(false);
  const [permResult, setPermResult] = useState(null);
  // V4.61 · Modal de falhas
  const [failuresOpen, setFailuresOpen] = useState(false);
  const [failures, setFailures] = useState([]);
  const [failuresLoading, setFailuresLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [c, m, t] = await Promise.all([
        api.get('/dnachat/whatsapp/settings'),
        api.get('/dnachat/whatsapp/metrics?hours=24').catch(() => ({ data: null })),
        api.get('/dnachat/whatsapp/templates').catch(() => ({ data: { items: [] } })),
      ]);
      // V4.58 · Nunca preenche inputs de segredo com valor do backend.
      // Zera os 3 campos de segredo; usuário vê ✓ configurado via flag *_configured.
      const s = c.data || {};
      setCfg({
        ...emptyCfg,
        business_account_id: s.business_account_id || '',
        phone_number_id: s.phone_number_id || '',
        api_version: s.api_version || 'v20.0',
        webhook_url: s.webhook_url || '',
        enabled: !!s.enabled,
        access_token: '',    // deixa vazio, mostra ✓ via flag
        verify_token: '',
        app_secret: '',
        access_token_configured: !!s.access_token_configured,
        webhook_verify_token_configured: !!s.webhook_verify_token_configured,
        app_secret_configured: !!s.app_secret_configured,
      });
      setConfigured(!!s.configured);
      setMetrics(m.data || null);
      setTemplates(t.data?.items || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Falha ao carregar config');
    } finally { setLoaded(true); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      // V4.58 · Envia SOMENTE campos com valor real. Segredos vazios são omitidos → backend preserva.
      const payload = {
        business_account_id: cfg.business_account_id || null,
        phone_number_id: cfg.phone_number_id || null,
        api_version: cfg.api_version || 'v20.0',
        webhook_url: cfg.webhook_url || null,
        enabled: !!cfg.enabled,
      };
      if (cfg.access_token && cfg.access_token.trim()) payload.access_token = cfg.access_token.trim();
      if (cfg.verify_token && cfg.verify_token.trim()) payload.verify_token = cfg.verify_token.trim();
      if (cfg.app_secret && cfg.app_secret.trim()) payload.app_secret = cfg.app_secret.trim();
      const { data } = await api.put('/dnachat/whatsapp/settings', payload);
      toast.success('Credenciais salvas com sucesso.');
      // Recarrega tudo (força reload real do backend)
      await fetchAll();
      return data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.provider_error?.error?.message || JSON.stringify(d));
      toast.error(`Não foi possível salvar as credenciais: ${msg}`);
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/dnachat/whatsapp/test-connection');
      setTestResult(data);
      if (data?.success) toast.success('Conexão válida com a Meta Cloud API.');
      else toast.error(data?.error || 'Falha no teste de conexão');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : JSON.stringify(d);
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  // V4.68 · Validar permissão de envio (scopes reais + acesso administrativo)
  const validateSend = async () => {
    setValidating(true);
    setPermResult(null);
    try {
      const { data } = await api.post('/dnachat/whatsapp/validate-send-permission');
      setPermResult(data);
      if (data?.send_permission_valid) toast.success('Permissão de envio autorizada ✓');
      else toast.error(data?.error_message || 'Sem permissão de envio — verifique a autorização do token Meta.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : JSON.stringify(d);
      setPermResult({ send_permission_valid: false, error_message: msg });
      toast.error(msg);
    } finally { setValidating(false); }
  };

  // V4.61 · Abrir modal de falhas + carregar detalhes
  const openFailures = async () => {
    setFailuresOpen(true);
    setFailuresLoading(true);
    try {
      const { data } = await api.get('/dnachat/whatsapp/failures?limit=50');
      setFailures(data?.items || []);
    } catch (e) {
      toast.error('Falha ao carregar detalhes');
    } finally { setFailuresLoading(false); }
  };

  const retryFailure = async (f) => {
    if (!f.retryable) return toast.error('Erro permanente — não pode ser reenviado automaticamente.');
    toast.info('Reenvie manualmente a partir do chat da conversa (as regras variam por causa).');
  };

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/dnachat/whatsapp/templates/sync');
      toast.success(`${data?.count || 0} templates sincronizados`);
      fetchAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : JSON.stringify(d));
    } finally { setSyncing(false); }
  };

  const copyWebhook = () => {
    const url = `${API_BASE}/api/dnachat/whatsapp/webhook`;
    navigator.clipboard?.writeText(url);
    toast.success('URL do webhook copiada');
  };

  const testPing = async () => {
    if (!testPhone.trim()) return toast.error('Informe o telefone');
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/dnachat/whatsapp/test-ping', { phone: testPhone.trim() });
      setTestResult(data);
      if (data?.success) {
        toast.success(`Ping enviado para ${data.phone_normalized} · ID ${String(data.provider_message_id || '').slice(0, 12)}`);
      } else {
        toast.error(data?.error || 'Falha ao enviar teste');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.provider_error?.error?.message || JSON.stringify(d));
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const kpi = (label, value, color = 'slate', onClick = null) => (
    <div
      className={`bg-white rounded-2xl border border-${color}-100 p-4 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-' + color + '-300 transition-all' : ''}`}
      onClick={onClick || undefined}
      data-testid={onClick ? `kpi-clickable-${label.toLowerCase().replace(/\s/g,'-')}` : undefined}
    >
      <div className={`text-[10px] uppercase tracking-wider text-slate-500 font-semibold ${onClick ? 'flex items-center gap-1' : ''}`}>
        {label}{onClick && <ChevronRight className="w-3 h-3" />}
      </div>
      <div className={`text-3xl font-bold text-${color}-700 mt-1`}>{value ?? '—'}</div>
    </div>
  );

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="whatsapp-config">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1">DNAChat · Omnichannel</div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-8 h-8 text-emerald-600" /> WhatsApp Business
          </h1>
          <p className="text-sm text-slate-500 mt-1">Integração com WhatsApp Cloud API. As respostas retornam automaticamente para o DNAChat da mesma conversa.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/v2/chat/whatsapp/queue')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-xs font-semibold"
            data-testid="wa-open-queue">
            <ListChecks className="w-3.5 h-3.5" /> Fila
          </button>
          {configured ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold" data-testid="wa-status-configured">
              <CheckCircle2 className="w-3.5 h-3.5" /> Configurado
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold" data-testid="wa-status-not-configured">
              <XCircle className="w-3.5 h-3.5" /> Não configurado
            </span>
          )}
          {cfg.enabled ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold" data-testid="wa-status-enabled">
              <Power className="w-3.5 h-3.5" /> Habilitado
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-500 text-xs font-semibold" data-testid="wa-status-disabled">
              <Power className="w-3.5 h-3.5" /> Desabilitado
            </span>
          )}
        </div>
      </div>

      {/* KPIs */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="wa-kpis">
          {kpi('Enviadas 24h', metrics.sent, 'emerald')}
          {kpi('Entregues', metrics.delivered, 'blue')}
          {kpi('Lidas', metrics.read, 'purple')}
          {kpi('Falhas', metrics.failed, 'rose', metrics.failed > 0 ? openFailures : null)}
          {kpi('Recebidas', metrics.inbound, 'amber')}
          {kpi('Fila', metrics.queue?.pending || 0, 'slate')}
        </div>
      )}

      {/* Config card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="wa-config-card">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-slate-500" />
          <h2 className="text-lg font-bold text-slate-800">Credenciais da Cloud API</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['business_account_id', 'Business Account ID', 'text', '123456789...', null],
            ['phone_number_id', 'Phone Number ID', 'text', '987654321...', null],
            ['access_token', 'Access Token *', 'password', 'EAAG...', 'access_token_configured'],
            ['verify_token', 'Webhook Verify Token *', 'password', 'string aleatória que você define', 'webhook_verify_token_configured'],
            ['app_secret', 'App Secret *', 'password', 'segredo do app (verificação HMAC)', 'app_secret_configured'],
            ['api_version', 'API Version', 'text', 'v20.0', null],
          ].map(([key, label, type, ph, flagKey]) => {
            const isConfigured = flagKey ? cfg[flagKey] : false;
            return (
              <div key={key}>
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</label>
                  {flagKey && isConfigured && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700" data-testid={`wa-cfg-${key}-configured`}>
                      <CheckCircle2 className="w-3 h-3" /> configurado
                    </span>
                  )}
                </div>
                <input
                  type={type}
                  value={cfg[key] || ''}
                  onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
                  placeholder={flagKey && isConfigured ? '••••••• (digite para substituir)' : ph}
                  className={`w-full mt-1 border rounded-lg px-3 py-2 text-sm font-mono ${flagKey && isConfigured ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`}
                  autoComplete="off"
                  data-testid={`wa-cfg-${key}`}
                />
                {flagKey && !isConfigured && (
                  <div className="text-[10px] text-amber-600 mt-1">✗ ainda não configurado</div>
                )}
                {flagKey && isConfigured && (
                  <div className="text-[10px] text-slate-500 mt-1">Deixe vazio para manter o valor salvo.</div>
                )}
              </div>
            );
          })}
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">URL do Webhook (configure na Meta)</label>
            <div className="flex gap-2 mt-1">
              <input readOnly value={`${API_BASE}/api/dnachat/whatsapp/webhook`}
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono bg-slate-50" data-testid="wa-webhook-url" />
              <button type="button" onClick={copyWebhook}
                className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold flex items-center gap-1"
                data-testid="wa-webhook-copy">
                <Copy className="w-4 h-4" /> Copiar
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Cole essa URL em <b>Meta for Developers → WhatsApp → Configuration → Webhook</b> junto com o Verify Token.</div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100 gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="wa-cfg-enabled" />
            <span className="text-sm text-slate-700 font-semibold">Habilitar envio via WhatsApp</span>
          </label>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={testConnection} disabled={testing || !configured}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-semibold disabled:opacity-50"
              title={!configured ? 'Salve as credenciais antes de testar' : 'Valida credenciais na Meta sem enviar mensagem'}
              data-testid="wa-cfg-test-connection">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Testar conexão
            </button>
            {/* V4.68 · Validar permissão de envio */}
            <button type="button" onClick={validateSend} disabled={validating || !configured}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-semibold disabled:opacity-50"
              title="Verifica se o token atual tem permissão de envio (whatsapp_business_messaging)"
              data-testid="wa-cfg-validate-send">
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Validar envio
            </button>
            <button type="button" onClick={save} disabled={saving || !loaded}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="wa-cfg-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando…</> : <><Save className="w-4 h-4" /> Salvar</>}
            </button>
          </div>
        </div>
        {/* V4.68 · Status separado: conexão vs permissão de envio */}
        {(cfg.send_permission_valid === false || cfg.last_permission_error) && (
          <div className="mt-3 rounded-lg p-3 bg-rose-50 border border-rose-200 text-xs text-rose-800" data-testid="wa-cfg-perm-warning">
            <div className="font-semibold mb-1 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Permissão de envio negada
              {cfg.last_permission_error?.code ? <span className="font-mono ml-1">(code {cfg.last_permission_error.code})</span> : null}
            </div>
            <div>{cfg.last_permission_error?.message || 'O Access Token atual não tem autorização suficiente. Atualize o token/permissões na Meta.'}</div>
            {cfg.last_permission_check_at && <div className="text-[10px] text-rose-500 mt-1">Última verificação: {new Date(cfg.last_permission_check_at).toLocaleString('pt-BR')}</div>}
          </div>
        )}
        {permResult && (
          <div className={`mt-3 rounded-lg p-3 border text-xs space-y-1 ${permResult.send_permission_valid ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`} data-testid="wa-cfg-perm-result">
            <div className="font-semibold flex items-center gap-1">
              {permResult.send_permission_valid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {permResult.send_permission_valid ? 'Envio autorizado' : 'Sem permissão de envio'}
              {permResult.error_code ? <span className="font-mono ml-1">(code {permResult.error_code})</span> : null}
            </div>
            {permResult.error_message && <div>{permResult.error_message}</div>}
            {permResult.actionable && <div className="italic">Ação: {permResult.actionable}</div>}
            {Array.isArray(permResult.permissions_found) && permResult.permissions_found.length > 0 && (
              <div className="text-[10px]"><b>Escopos:</b> {permResult.permissions_found.join(', ')}</div>
            )}
            {permResult.token_fingerprint && <div className="text-[10px] font-mono opacity-70">token_fp: {permResult.token_fingerprint}</div>}
          </div>
        )}
        {testResult && testResult.checks && (
          <div className="mt-3 rounded-lg p-3 bg-slate-50 border border-slate-200 space-y-1" data-testid="wa-cfg-test-result">
            {testResult.checks.map((c, i) => (
              <div key={i} className={`text-xs flex items-start gap-1.5 ${c.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 mt-0.5" />}
                <span><b>{c.name}:</b> {typeof c.detail === 'string' ? c.detail : JSON.stringify(c.detail).slice(0, 300)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* V4.56 · Teste ping-pong */}
      <div className="bg-white rounded-2xl border border-emerald-200 p-5 mb-6" data-testid="wa-test-card">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-slate-800">Testar integração (ping-pong)</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">Envia uma mensagem de validação para o número informado. Use seu próprio celular antes do primeiro broadcast real.</p>
        <div className="flex gap-2 items-start">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="(11) 91234-5678 ou 5511912345678"
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
            data-testid="wa-test-phone"
          />
          <button
            type="button"
            onClick={testPing}
            disabled={testing || !cfg.enabled}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="wa-test-send"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Enviar teste
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 rounded-lg p-3 text-sm ${testResult.success ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-rose-50 border border-rose-200 text-rose-800'}`} data-testid="wa-test-result">
            {testResult.success ? (
              <div>
                <div className="font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Mensagem enviada</div>
                <div className="text-xs mt-1">Número normalizado: <code>{testResult.phone_normalized}</code></div>
                <div className="text-xs">Provider ID: <code>{testResult.provider_message_id}</code></div>
              </div>
            ) : (
              <div>
                <div className="font-semibold flex items-center gap-1.5"><XCircle className="w-4 h-4" /> Falha no teste</div>
                <div className="text-xs mt-1 font-mono">{String(testResult.error || '').slice(0, 400)}</div>
              </div>
            )}
          </div>
        )}
        {!cfg.enabled && (
          <div className="text-[11px] text-amber-700 mt-2">⚠ Habilite o envio WhatsApp no card acima antes de testar.</div>
        )}
      </div>

      {/* Templates */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" data-testid="wa-templates">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-bold text-slate-800">Templates ({templates.length})</h2>
          </div>
          <button type="button" onClick={syncTemplates} disabled={syncing || !configured}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-900 disabled:opacity-50"
            data-testid="wa-templates-sync">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sincronizar com Meta
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Nome</th>
              <th className="text-left px-4 py-2">Categoria</th>
              <th className="text-left px-4 py-2">Idioma</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Sincronizado em</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">
                Nenhum template. Clique em <b>Sincronizar com Meta</b> após configurar as credenciais.
              </td></tr>
            )}
            {templates.map((t, i) => (
              <tr key={`${t.name}-${t.language}`} className="border-t border-slate-100" data-testid={`wa-template-${i}`}>
                <td className="px-4 py-2 font-semibold text-slate-800">{t.name}</td>
                <td className="px-4 py-2 text-slate-600">{t.category}</td>
                <td className="px-4 py-2 text-slate-600 font-mono">{t.language}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                    t.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                    t.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                    'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>{t.status || '—'}</span>
                </td>
                <td className="px-4 py-2 text-[11px] text-slate-500">{t.synced_at ? new Date(t.synced_at).toLocaleString('pt-BR') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Helper */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-slate-700 flex items-start gap-3">
        <Link2 className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <div className="font-semibold text-blue-900">Como obter as credenciais</div>
          <ol className="mt-1 text-xs list-decimal ml-4 space-y-0.5">
            <li>Crie um app em <b>developers.facebook.com</b> → Adicione o produto <b>WhatsApp</b>.</li>
            <li>Copie o <b>Phone Number ID</b> e o <b>Business Account ID</b> (WABA).</li>
            <li>Gere um <b>Access Token permanente</b> (System User) e cole em Access Token.</li>
            <li>Defina um <b>Verify Token</b> aqui e o mesmo na Meta → Webhook → Configuration.</li>
            <li>Copie o <b>App Secret</b> (Basic Settings do app) — usado para assinatura HMAC do webhook.</li>
            <li>Assine os webhooks <b>messages</b> e <b>message_status</b>.</li>
          </ol>
        </div>
      </div>

      {/* V4.61 · Modal de detalhamento de falhas */}
      {failuresOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setFailuresOpen(false)} data-testid="wa-failures-modal">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                <h3 className="text-lg font-bold text-slate-800">Falhas de envio WhatsApp</h3>
              </div>
              <button onClick={() => setFailuresOpen(false)} className="p-1 rounded hover:bg-slate-100" data-testid="wa-failures-close">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {failuresLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : failures.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">Nenhuma falha registrada.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {failures.map((f) => (
                    <div key={f.id} className="p-4 hover:bg-slate-50" data-testid={`wa-failure-${f.id.slice(0,8)}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold text-slate-800">{f.guardian_name || '—'}</div>
                            <span className="text-xs font-mono text-slate-500">+{f.phone}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {new Date(f.created_at).toLocaleString('pt-BR')}
                            {f.template_name && <> · template: <code>{f.template_name}</code></>}
                          </div>
                          {f.body && <div className="text-xs text-slate-600 mt-1 italic max-w-2xl truncate">&ldquo;{f.body}&rdquo;</div>}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border ${
                              f.category === 'window' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                              f.category === 'auth' ? 'bg-rose-50 border-rose-200 text-rose-700' :
                              f.category === 'recipient' ? 'bg-orange-50 border-orange-200 text-orange-700' :
                              f.category === 'template' ? 'bg-purple-50 border-purple-200 text-purple-700' :
                              f.category === 'rate_limit' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                              'bg-slate-100 border-slate-200 text-slate-600'
                            }`}>{f.category || 'unknown'}</span>
                            <span className="text-[11px] font-mono text-slate-500">código {f.meta_error_code ?? '—'}</span>
                            {f.fbtrace_id && <span className="text-[10px] font-mono text-slate-400" title="fbtrace_id">trace: {String(f.fbtrace_id).slice(0,10)}…</span>}
                          </div>
                          <div className="text-sm text-slate-800 mt-1"><b>Motivo:</b> {f.cause}</div>
                          {f.meta_error_message && f.meta_error_message !== f.cause && (
                            <div className="text-[11px] text-slate-500 font-mono mt-0.5">{f.meta_error_message}</div>
                          )}
                        </div>
                        {f.retryable && (
                          <button onClick={() => retryFailure(f)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold"
                            data-testid={`wa-failure-retry-${f.id.slice(0,8)}`}>
                            <RotateCw className="w-3.5 h-3.5" /> Reenviar
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
